<template><div><h2 id="scrollbar-directive" tabindex="-1" class="d-docsite--header-2">Scrollbar Directive <a class="header-anchor d-link d-docsite--link d-link" href="#scrollbar-directive" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Allows to add overlay scrollbars that will look the same for every browser. The directive sets up the scrollbars from the library <a href="https://kingsora.github.io/OverlayScrollbars/" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">OverlayScrollbars</a>.</p>
<code-well-header>
  <div class="d-hmx164 d-w30p d-bar8 d-ba d-bc-default" v-dt-scrollbar>
    <dt-stack>
      <div v-for="item in items" class="item">
        {{ item}}
      </div>
    </dt-stack>
  </div>
</code-well-header>
<code-example-tabs
htmlCode='
<div class="d-hmx164 d-w30p d-bar8 d-ba d-bc-default d-scrollbar" data-overlayscrollbars="host"
  data-overlayscrollbars-initialize="true">
  <div class="os-size-observer">
    <div class="os-size-observer-listener"></div>
  </div>
  <div data-overlayscrollbars-viewport="scrollbarHidden overflowXHidden overflowYScroll" tabindex="-1">
    <div class="item">user 1</div>
    ...
  </div>
  <div class="os-scrollbar os-scrollbar-horizontal os-theme-dark os-scrollbar-auto-hide
  os-scrollbar-handle-interactive os-scrollbar-cornerless os-scrollbar-unusable">
    <div class="os-scrollbar-track">
      <div class="os-scrollbar-handle"></div>
    </div>
  </div>
  <div class="os-scrollbar os-scrollbar-vertical os-theme-dark os-scrollbar-auto-hide
  os-scrollbar-handle-interactive os-scrollbar-visible os-scrollbar-cornerless">
    <div class="os-scrollbar-track">
      <div class="os-scrollbar-handle"></div>
    </div>
  </div>
</div>
'
vueCode='
<div class="d-hmx164 d-w30p d-bar8 d-ba d-bc-default" v-dt-scrollbar>
  <dt-stack>
    <div v-for="item in items" class="item">
      {{ item}}
    </div>
  </dt-stack>
</div>
'
/>
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Import the directive and styling from dialtone</p>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token keyword">import</span> <span class="token punctuation">{</span> DtScrollbarDirective <span class="token punctuation">}</span> <span class="token keyword">from</span> <span class="token string">"@dialpad/dialtone/vue"</span><span class="token punctuation">;</span></span>
<span class="line"></span>
<span class="line"><span class="token comment">// Import styling</span></span>
<span class="line"><span class="token keyword">import</span> <span class="token string">'overlayscrollbars/overlayscrollbars.css'</span><span class="token punctuation">;</span></span>
<span class="line"></span></code></pre>
</div><p class="d-docsite--paragraph">Install the directive into vue instance</p>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line">app<span class="token punctuation">.</span><span class="token function">use</span><span class="token punctuation">(</span>DtScrollbarDirective<span class="token punctuation">)</span><span class="token punctuation">;</span></span>
<span class="line"></span></code></pre>
</div><p class="d-docsite--paragraph">To add a custom overlay scrollbar to a scrollable region, apply the <code v-pre>v-dt-scrollbar</code> directive to the parent element of the desired region.
This parent element should have one and only one child. In the case where there are siblings, the scrollable element should be wrapped inside a new <code v-pre>&lt;div&gt;</code> tag with the directive attached by adding <code v-pre>&lt;div v-dt-scrollbar&gt;&lt;/div&gt;</code> around the element.
There is no need to explicitly add an <code v-pre>overflow</code> property. If the section overflows the available vertical space, a vertical scrollbar will be present. Similarly, if it exceeds the horizontal space, a horizontal scrollbar will appear.</p>
<h2 id="characteristics" tabindex="-1" class="d-docsite--header-2">Characteristics <a class="header-anchor d-link d-docsite--link d-link" href="#characteristics" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Has an overlay style: it appears on top of the content rather than the scrollbar taking up space within the container.</li>
<li class="d-docsite--list-element">It grows when hovering the scrollbar handle for better accessibility.</li>
<li class="d-docsite--list-element">Appears when the mouse enters the scrollable area and disappears on mouse out after a certain time. This can be customized,
see <a href="#variants" class="d-docsite--link d-link">variants</a>.</li>
<li class="d-docsite--list-element">The look and feel will be the same for every browser and OS.</li>
<li class="d-docsite--list-element">Emulates a browser's native scrollbar keyboard and mouse events.</li>
</ul>
<h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">To customize the behavior of the scrollbar, you can use different arguments with the directive. The allowed arguments are 'leave' (default), 'never', 'scroll', and 'move'.</p>
<h3 id="enter-default" tabindex="-1" class="d-docsite--header-3">Enter (Default) <a class="header-anchor d-link d-docsite--link d-link" href="#enter-default" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Show the scrollbar when the mouse enters the scrollable area. This is the default option, so no argument is needed.</p>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token operator">&lt;</span>div v<span class="token operator">-</span>dt<span class="token operator">-</span>scrollbar<span class="token operator">></span><span class="token operator">&lt;</span><span class="token operator">/</span>div<span class="token operator">></span></span>
<span class="line"></span></code></pre>
</div><code-well-header>
  <div class="d-hmx164 d-w30p d-bar8 d-ba d-bc-default" v-dt-scrollbar>
    <dt-stack>
      <div v-for="item in items" class="item">
        {{ item}}
      </div>
    </dt-stack>
  </div>
</code-well-header>
<h3 id="always" tabindex="-1" class="d-docsite--header-3">Always <a class="header-anchor d-link d-docsite--link d-link" href="#always" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Always show the scrollbar if the region is overflowing the available space.</p>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token operator">&lt;</span>div v<span class="token operator">-</span>dt<span class="token operator">-</span>scrollbar<span class="token operator">:</span>never<span class="token operator">></span><span class="token operator">&lt;</span><span class="token operator">/</span>div<span class="token operator">></span></span>
<span class="line"></span></code></pre>
</div><code-well-header>
  <div class="d-hmx164 d-w30p d-bar8 d-ba d-bc-default" v-dt-scrollbar:never>
    <dt-stack>
      <div v-for="item in items" class="item">
        {{ item}}
      </div>
    </dt-stack>
  </div>
</code-well-header>
<h3 id="scroll" tabindex="-1" class="d-docsite--header-3">Scroll <a class="header-anchor d-link d-docsite--link d-link" href="#scroll" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Show the scrollbar on scroll.</p>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token operator">&lt;</span>div v<span class="token operator">-</span>dt<span class="token operator">-</span>scrollbar<span class="token operator">:</span>scroll<span class="token operator">></span><span class="token operator">&lt;</span><span class="token operator">/</span>div<span class="token operator">></span></span>
<span class="line"></span></code></pre>
</div><code-well-header>
  <div class="d-hmx164 d-w30p d-bar8 d-ba d-bc-default" v-dt-scrollbar:scroll>
    <dt-stack>
      <div v-for="item in items" class="item">
        {{ item}}
      </div>
    </dt-stack>
  </div>
</code-well-header>
<h3 id="move" tabindex="-1" class="d-docsite--header-3">Move <a class="header-anchor d-link d-docsite--link d-link" href="#move" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Show the scrollbar when the mouse moves inside the scrollable area.</p>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token operator">&lt;</span>div v<span class="token operator">-</span>dt<span class="token operator">-</span>scrollbar<span class="token operator">:</span>move<span class="token operator">></span><span class="token operator">&lt;</span><span class="token operator">/</span>div<span class="token operator">></span></span>
<span class="line"></span></code></pre>
</div><code-well-header>
  <div class="d-hmx164 d-w30p d-bar8 d-ba d-bc-default" v-dt-scrollbar:move>
    <dt-stack>
      <div v-for="item in items" class="item">
        {{ item}}
      </div>
    </dt-stack>
  </div>
</code-well-header>
<h2 id="limitations" tabindex="-1" class="d-docsite--header-2">Limitations <a class="header-anchor d-link d-docsite--link d-link" href="#limitations" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Adding this directive to a DOM element or a Vue component will alter the DOM structure, by adding four elements inside the one that the directive was attached to. If the scrollable region is a Vue component, it's recommended to wrap it in a <code v-pre>&lt;div v-dt-scrollbar&gt;&lt;/div&gt;</code>, to avoid altering the structure that the component needs.</p>
<p class="d-docsite--paragraph">The added elements are:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">One with the class <code v-pre>os-size-observer</code></li>
<li class="d-docsite--list-element">The second one is the scrollable viewport</li>
<li class="d-docsite--list-element">The horizontal scrollbar</li>
<li class="d-docsite--list-element">The vertical scrollbar</li>
</ul>
<p class="d-docsite--paragraph">This can make it challenging to use with components that rely on event listeners or may even render it unusable.</p>
</div></template>


<script setup>
  const items = [
    'user 1',
    'user 2',
    'user 3',
    'user 4',
    'user 5',
    'user 6',
    'user 7',
    'user 8',
    'user 9',
    'user 10',
    'user 11',
    'user 12',
    'user 13',
    'user 14',
    'user 15',
  ]
</script>
<style lang="less" scoped>
.item {
  padding: var(--dt-space-300) var(--dt-space-400);
  border-bottom: var(--dt-size-border-100) solid var(--dt-color-border-default);
  &:last-child {
    border-bottom: none;
  }
}
</style>